import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router";
import { useToast } from "../../../components/ui/Toast";
import { TierEditor, validateTiers } from "./TierEditor";
import type { TierDraft } from "./TierEditor";
import {
  ALL_PRICING_PLANS,
  CREATE_BOOKING_TYPE,
  ALL_BOOKING_TYPES,
} from "./queries";
import type { PricingPlanRecord, BookingTypeRecord } from "./types";
import { STATE_OPTIONS, CUSTOMER_COUNT_TYPE_OPTIONS } from "./types";
import {
  ALL_AGREEMENT_TYPES,
  type AgreementTypeRecord,
} from "../../../apollo/agreementTypes";

export function BookingTypeCreate() {
  const navigate = useNavigate();
  const toast = useToast();

  const ppQuery = useQuery<{ allPricingPlans: PricingPlanRecord[] }>(
    ALL_PRICING_PLANS,
    { fetchPolicy: "cache-and-network" },
  );
  const plans = ppQuery.data?.allPricingPlans ?? [];

  // Load AgreementTypes for the document picker
  const atQuery = useQuery<{ allAgreementTypes: AgreementTypeRecord[] }>(
    ALL_AGREEMENT_TYPES,
    {
      variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC", filter: { status: "active" } },
      fetchPolicy: "cache-and-network",
    },
  );
  const agreementTypes = atQuery.data?.allAgreementTypes ?? [];

  const [createBookingType, { loading: saving }] =
    useMutation(CREATE_BOOKING_TYPE);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState<number>(1);
  const [state, setState] = useState("");
  const [customerCountType, setCustomerCountType] = useState("FIXED");
  const [minCustomers, setMinCustomers] = useState(1);
  const [maxCustomers, setMaxCustomers] = useState(1);
  const [agreementTypeId, setAgreementTypeId] = useState("");
  const [tiers, setTiers] = useState<TierDraft[]>([]);

  const tierValidation = useMemo(
    () => validateTiers(tiers, minCustomers, maxCustomers),
    [tiers, minCustomers, maxCustomers],
  );

  // Find selected AgreementType for dual-write
  const selectedType = useMemo(
    () => agreementTypes.find((t) => t.id === agreementTypeId),
    [agreementTypes, agreementTypeId],
  );

  const canSave =
    name.trim().length > 0 &&
    // A service with no document hard-fails the booking funnel (no agreement to
    // sign) — the failure class #444 is about. Require a document explicitly.
    agreementTypeId.length > 0 &&
    // Require the selected type to be resolvable so dual-write never sends an
    // empty agreementType string (e.g. list still loading / type deleted).
    selectedType != null &&
    !saving &&
    (tiers.length === 0 || tierValidation.valid);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;

    try {
      const result = await createBookingType({
        variables: {
          name: name.trim(),
          description: description.trim() || null,
          slug: slug.trim() || null,
          isActive,
          sortOrder,
          state: state || null,
          customerCountType,
          minCustomers,
          maxCustomers,
          // Dual-write: agreementTypeId + agreementType (legacy string = typeKey)
          agreementTypeId,
          agreementType: selectedType?.typeKey ?? "",
          pricingPlans: tiers.map((t) => ({
            pricingPlanId: t.pricingPlanId,
            minCustomerCount: t.minCustomerCount,
            maxCustomerCount: t.maxCustomerCount,
          })),
        },
        refetchQueries: [{ query: ALL_BOOKING_TYPES }],
      });
      const created = result.data?.createBookingType as
        | BookingTypeRecord
        | undefined;
      if (created) {
        toast.push({
          kind: "success",
          title: `${created.name} created`,
          description: "The new service is ready.",
        });
        navigate(`/settings/catalog/services/${created.id}`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't create service",
        description: msg,
      });
    }
  }

  return (
    <div className="max-w-2xl">
      <form onSubmit={handleSubmit} className="px-7 py-8 space-y-6">
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-1">
            New service
          </div>
          <h2 className="font-display text-[26px] font-medium tracking-tight">
            Create a service
          </h2>
          <p className="text-[12.5px] text-ink-3 mt-1.5">
            Set up the basics, then add pricing tiers. The service starts
            as <span className="font-medium">active</span> and will appear on
            the public booking page.
          </p>
        </div>

        {/* Basic info */}
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Service details
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Name *
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Estate planning"
                autoFocus
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>

            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Description
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Initial consultation for trust and will planning"
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40 resize-y"
              />
            </label>

            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Slug
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="individual-estate"
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
              <span className="text-[10.5px] text-ink-5 mt-0.5 block">
                Unique identifier used in the public booking URL.
              </span>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  State
                </span>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                >
                  {STATE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Document *
                </span>
                <select
                  value={agreementTypeId}
                  onChange={(e) => setAgreementTypeId(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                >
                  <option value="">Select agreement type...</option>
                  {agreementTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Sort order
                </span>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>

              <label className="flex items-center gap-2 mt-5">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 rounded border-ink-5 text-tide focus:ring-tide/40"
                />
                <span className="text-[12.5px]">Active</span>
              </label>
            </div>
          </div>
        </div>

        {/* Customer configuration */}
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Customer configuration
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Customer count type
              </span>
              <select
                value={customerCountType}
                onChange={(e) => setCustomerCountType(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              >
                {CUSTOMER_COUNT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Min customers
                </span>
                <input
                  type="number"
                  value={minCustomers}
                  onChange={(e) =>
                    setMinCustomers(parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                  max={10}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Max customers
                </span>
                <input
                  type="number"
                  value={maxCustomers}
                  onChange={(e) =>
                    setMaxCustomers(parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                  max={10}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Pricing tiers */}
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Pricing tiers
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5">
            <TierEditor
              tiers={tiers}
              onChange={setTiers}
              plans={plans}
              minCustomers={minCustomers}
              maxCustomers={maxCustomers}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="hair-t border-t pt-5 flex items-center justify-end gap-2">
          <Link
            to="/settings/catalog"
            className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2"
          >
            Discard
          </Link>
          <button
            type="submit"
            disabled={!canSave}
            className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
          >
            {saving ? "Creating..." : "Create service"}
          </button>
        </div>
      </form>
    </div>
  );
}
